/**
 * Quantum Forge Loader - Lazy loader for Quantum Forge WASM module
 *
 * Defers WASM loading from the critical path while preloading in background
 * so it's ready when needed. If not ready, callers can show loading UI.
 * 
 * IMPORTANT: Quantum Forge is built from source and copied to dist/ as
 * quantum-forge-web-api.mjs (not an npm package). Run 'npm run setup' to build.
 */

import type { LoggerInterface } from "../logging/Logger";

// Type for the quantum forge module
type QuantumForgeModuleType = typeof import("./quantum-forge-api.mjs");

// Configurable base path for WASM artifacts
let wasmBasePath = "/quantum-forge";

/**
 * Set the base URL path where Quantum Forge WASM files are served.
 * Default is "/quantum-forge" which matches the Vite plugin's serve path.
 * Consumers using the Vite plugin don't need to call this.
 */
export function setWasmBasePath(path: string): void {
  wasmBasePath = path.endsWith("/") ? path.slice(0, -1) : path;
}

/**
 * Select a named WASM build variant (e.g. "d7n10").
 * Sugar for `setWasmBasePath("/quantum-forge-{name}")`.
 *
 * Must be called before `ensureLoaded()`. If the module is already loaded,
 * a warning is logged and the call is ignored.
 */
export function useQuantumForgeBuild(name: string): void {
  if (isInitialized) {
    logger?.warn?.(
      `useQuantumForgeBuild("${name}") called after module already loaded — ignoring. Call before ensureLoaded().`,
      "QuantumForgeLoader",
    );
    return;
  }
  setWasmBasePath(`/quantum-forge-${name}`);
}

// Cache the module and initialization state
let quantumForgeModule: QuantumForgeModuleType | null = null;
let initPromise: Promise<void> | null = null;
let isInitialized = false;
let loadStarted = false;

// Logger reference (set during startBackgroundLoad)
let logger: LoggerInterface | undefined;

/**
 * Start loading the WASM module in the background.
 * Call this after the page has rendered (e.g., after DOMContentLoaded or initial paint).
 */
export function startBackgroundLoad(loggerRef?: LoggerInterface): void {
  if (loadStarted) return;
  loadStarted = true;
  logger = loggerRef;

  // Use requestIdleCallback if available, otherwise setTimeout
  const scheduleLoad = (callback: () => void) => {
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 100);
    }
  };

  scheduleLoad(() => {
    logger?.info?.("Starting background Quantum Forge load", "QuantumForgeLoader");
    // Trigger the load but don't await - let it happen in background
    ensureLoaded().catch((err) => {
      logger?.warn?.(
        `Background Quantum Forge load failed: ${err?.message ?? err}`,
        "QuantumForgeLoader",
      );
    });
  });
}

/**
 * Ensure Quantum Forge is loaded and initialized.
 * Returns a promise that resolves when the module is ready.
 * Can be called multiple times - will return the same promise.
 */
export async function ensureLoaded(): Promise<void> {
  if (isInitialized) return;

  if (initPromise) {
    await initPromise;
    return;
  }

  initPromise = (async () => {
    const startTime = performance.now();
    logger?.info?.("Loading Quantum Forge WASM module...", "QuantumForgeLoader");

    // Dynamic import of Quantum Forge WASM module from the configured base path
    const modulePath = `${wasmBasePath}/quantum-forge-web-api.mjs`;
    const mod = (await import(/* @vite-ignore */ modulePath)) as QuantumForgeModuleType;
    quantumForgeModule = mod;

    // Initialize the WASM
    await mod.QuantumForge.initialize();

    const version = mod.QuantumForge.getVersion();
    const maxDim = mod.QuantumForge.getMaxDimension();
    const maxQudits = mod.QuantumForge.getMaxQudits();
    const elapsed = (performance.now() - startTime).toFixed(0);

    logger?.info?.(
      `Quantum Forge v${version} ready in ${elapsed}ms (max dim: ${maxDim}, max qudits: ${maxQudits})`,
      "QuantumForgeLoader",
    );

    console.log(
      "%c\u269B Powered by Quantum Forge %c quantumnative.io ",
      "background: #6366f1; color: white; padding: 2px 6px; border-radius: 3px 0 0 3px; font-weight: bold;",
      "background: #1e1b4b; color: #c7d2fe; padding: 2px 6px; border-radius: 0 3px 3px 0;",
    );

    isInitialized = true;
  })();

  // Handle errors by clearing the promise so retry is possible
  initPromise.catch(() => {
    initPromise = null;
  });

  await initPromise;
}

/**
 * Check if Quantum Forge is ready to use (non-blocking).
 */
export function isReady(): boolean {
  return isInitialized;
}

/**
 * Get the loaded module. Throws if not loaded.
 * For synchronous access after ensuring it's loaded.
 */
export function getModule(): typeof import("./quantum-forge-api.mjs") {
  if (!quantumForgeModule || !isInitialized) {
    throw new Error("QuantumForge not loaded. Call ensureLoaded() first and await it.");
  }
  return quantumForgeModule;
}

/**
 * Get the QuantumForge class from the loaded module.
 */
export function getQuantumForge(): typeof import("./quantum-forge-api.mjs").QuantumForge {
  return getModule().QuantumForge;
}

/**
 * Convenience re-exports for common operations.
 * These will throw if module not loaded.
 */
export function getVersion(): string {
  return getQuantumForge().getVersion();
}

export function getMaxDimension(): number {
  return getQuantumForge().getMaxDimension();
}

export function getMaxQudits(): number {
  return getQuantumForge().getMaxQudits();
}

export function getMaxStateSize(): number {
  return getQuantumForge().getMaxStateSize();
}

/**
 * Get the WASM memory bytes (for analytics).
 */
export function getWasmMemoryBytes(): number | null {
  if (!isInitialized) return null;
  try {
    const qf = getQuantumForge() as any;
    return typeof qf.getMemoryBytes === "function" ? qf.getMemoryBytes() : null;
  } catch {
    return null;
  }
}

/**
 * Get the required attribution text for display in your application.
 * Include this in a user-visible location (credits screen, about page, etc.).
 */
export function getAttribution(): string {
  return "Powered by Quantum Forge \u2014 \u00A9 Quantum Native \u2014 quantumnative.io";
}

/**
 * Register the Quantum Forge service worker for offline WASM caching.
 * Call once from your game controller after page load.
 * The SW caches WASM artifacts on first fetch so subsequent loads work offline.
 *
 * @param swPath - Path to the service worker file. Default: "/quantum-forge-sw.js"
 */
export async function registerServiceWorker(
  swPath = "/quantum-forge-sw.js",
): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(swPath);
    logger?.info?.(`Service worker registered (scope: ${reg.scope})`, "QuantumForgeLoader");
    return reg;
  } catch (err) {
    logger?.warn?.(
      `Service worker registration failed: ${err instanceof Error ? err.message : err}`,
      "QuantumForgeLoader",
    );
    return null;
  }
}
